import { createApp } from 'vue';
import { createPinia } from 'pinia';
import App from './App.vue';
import router from './router';
import { registerAllIcons } from '@/common/utils/iconsRegistry';
import { library } from '@fortawesome/fontawesome-svg-core';
import { far } from '@fortawesome/free-regular-svg-icons';

import { preloadCriticalRoutes } from '@/common/utils/routePreloader';
import { logger } from '@/common/services/config';
import { storageService } from './common/services/storage/storageService';
import { GlobalMockupService } from '@/common/services/mockup';
import i18n from './i18n';
import VueMatomo from 'vue-matomo';
import { matomoConfig } from '@/common/services/monitoring/matomo';

import deviceDetectionPlugin from '@/plugins/deviceDetection';
import configPlugin from '@/plugins/config';

import { IonicVue } from '@ionic/vue';

import { StoryblokVue, apiPlugin } from '@storyblok/vue';
import { registerStoryblokComponents, storyblokComponents } from '@/features/storyblok';

/* Core CSS required for Ionic components to work properly */
import '@ionic/vue/css/core.css';

/* Basic CSS for apps built with Ionic */
import '@ionic/vue/css/normalize.css';
import '@ionic/vue/css/structure.css';
import '@ionic/vue/css/typography.css';

/* Optional CSS utils that can be commented out */
import '@ionic/vue/css/padding.css';
import '@ionic/vue/css/float-elements.css';
import '@ionic/vue/css/text-alignment.css';
import '@ionic/vue/css/text-transformation.css';
import '@ionic/vue/css/flex-utils.css';
import '@ionic/vue/css/display.css';

import '@ionic/vue/css/palettes/dark.system.css';
import './theme/variables.css';

import { authService } from '@/features/common/auth/services/authService';
import { ApiClientBase } from '@/common/services/api/ApiClientBase';

// Development: Import theme test utility, cookie cleanup, and MSAL cache manager
if (import.meta.env.DEV) {
  import('@/common/utils/cookieCleanup');
  import('@/features/common/auth/services/msalCacheManager');
}
import { initializeTelemetry } from '@/telemetry';
import { useMatomoTracking } from '@/common/composables/useMatomoTracking';

const pinia = createPinia();

// Expose logger globally to avoid repetitive imports in components/composables.
globalThis.logger = logger;

// Initialisation d'Application Insights
initializeTelemetry();

// Add FontAwesome icons to library
library.add(far);

const app = createApp(App);

registerStoryblokComponents(app, storyblokComponents);

app
  .use(IonicVue) // Ensure IonicVue is used
  .use(configPlugin) // Configuration d'environnement
  .use(pinia)
  .use(router)
  .use(i18n)
  .use(deviceDetectionPlugin)
  .use(VueMatomo, matomoConfig)
  .use(StoryblokVue, {
    accessToken: import.meta.env.VITE_STORYBLOK_DELIVERY_API_TOKEN_PREVIEW,
    apiOptions: {
      region: 'eu',
    },
    use: [apiPlugin],
  });

// Register all icons used throughout the application
registerAllIcons();

// Initialize global mockup service
GlobalMockupService.initializeMockupMode();
GlobalMockupService.initializeFeatureSettings();

// Preload critical routes for better performance
preloadCriticalRoutes();

// Wire token refresh into all API clients for transparent silent SSO
ApiClientBase.setTokenRefreshFn((forceRefresh) => authService.getAccessToken(forceRefresh));

// Initialize storage service and migrate any existing localStorage data
storageService.migrateFromLocalStorage();
logger.log(`Using ${storageService.getStorageType()} for data persistence`);

// Initialize Matomo tracking for all pages
useMatomoTracking(router);

// Mount the app immediately - auth initialization happens in background
// The auth guard in the router will wait for initialization to complete
router.isReady().then(() => {
  app.mount('#app');
});

// Start auth initialization in parallel (auth guard awaits it via waitForInitialization)
authService.initialize();
